import { catalogIpsum } from './catalogIpsum';

const isNextFieldAttribute = (rest) => {
  const trimmed = String(rest || '').trim();
  const match = trimmed.match(/^([a-z_][a-z0-9_]*)\s*=/i);
  if (!match) return false;
  const key = match[1].toLowerCase();
  return ['type', 'label', 'default', 'demo_count', 'min_items', 'max_items', 'chance'].includes(key);
};

const readMockQuotedValue = (src) => {
  const quote = src[0];
  let i = 1;
  let value = '';
  while (i < src.length) {
    const ch = src[i];
    if (ch === '\\' && i + 1 < src.length) {
      value += src[i + 1];
      i += 2;
      continue;
    }
    if (ch === quote) {
      return { value, consumed: i + 1 };
    }
    value += ch;
    i += 1;
  }
  throw new Error('unclosed quoted value');
};

export const readMockExpression = (src) => {
  const trimmed = String(src || '').trim();
  if (!trimmed.toLowerCase().startsWith('mock.')) {
    throw new Error('expected mock expression');
  }
  let depth = 0;
  let i = 0;
  while (i < trimmed.length) {
    const ch = trimmed[i];
    if (ch === '(') depth += 1;
    if (ch === ')') depth -= 1;
    i += 1;
    if (depth <= 0 && i < trimmed.length) {
      const rest = trimmed.slice(i);
      if (!rest.trim() || isNextFieldAttribute(rest)) break;
    }
  }
  if (depth > 0) throw new Error('unclosed mock expression');
  return { value: trimmed.slice(0, i).trim(), consumed: i };
};

export const parseFieldTagAttributes = (rest) => {
  const attrs = {};
  let cursor = String(rest || '').trim();
  while (cursor) {
    const eq = cursor.indexOf('=');
    if (eq <= 0) break;
    const key = cursor.slice(0, eq).trim().toLowerCase();
    cursor = cursor.slice(eq + 1).trim();
    if (!cursor) break;
    let value = '';
    let consumed = 0;
    if (cursor.toLowerCase().startsWith('mock.')) {
      const parsed = readMockExpression(cursor);
      value = parsed.value;
      consumed = parsed.consumed;
    } else if (cursor[0] === '"' || cursor[0] === "'") {
      const parsed = readMockQuotedValue(cursor);
      value = parsed.value;
      consumed = parsed.consumed;
    } else {
      break;
    }
    attrs[key] = value;
    cursor = cursor.slice(consumed).trim();
  }
  return attrs;
};

const parseMockArgs = (src) => {
  const out = {};
  let cursor = String(src || '').trim();
  while (cursor) {
    const colon = cursor.indexOf(':');
    if (colon <= 0) throw new Error('invalid mock argument');
    const key = cursor.slice(0, colon).trim().toLowerCase();
    cursor = cursor.slice(colon + 1).trim();
    let value = '';
    let consumed = 0;
    if (cursor[0] === '"' || cursor[0] === "'") {
      const parsed = readMockQuotedValue(cursor);
      value = parsed.value;
      consumed = parsed.consumed;
    } else {
      const comma = cursor.indexOf(',');
      if (comma < 0) {
        value = cursor.trim();
        consumed = cursor.length;
      } else {
        value = cursor.slice(0, comma).trim();
        consumed = comma + 1;
      }
    }
    out[key] = value;
    cursor = cursor.slice(consumed).trim();
    if (cursor.startsWith(',')) cursor = cursor.slice(1).trim();
  }
  return out;
};

export const parseMockCall = (expr) => {
  const trimmed = String(expr || '').trim();
  if (!trimmed.toLowerCase().startsWith('mock.')) {
    throw new Error('not a mock expression');
  }
  const rest = trimmed.slice(5).trim();
  const paren = rest.indexOf('(');
  let name = rest;
  let args = {};
  if (paren >= 0) {
    name = rest.slice(0, paren).trim();
    const close = rest.lastIndexOf(')');
    if (close < paren) throw new Error('unclosed mock call');
    const argStr = rest.slice(paren + 1, close).trim();
    if (argStr) args = parseMockArgs(argStr);
  }
  name = name.toLowerCase().trim();
  if (!name) throw new Error('mock method name required');
  return { name, args };
};

const parseMockSize = (raw) => {
  const normalized = String(raw || '').trim().toLowerCase().replace(/\s/g, '');
  if (!normalized) return [0, 0];
  const parts = normalized.split('x');
  if (parts.length !== 2) return [0, 0];
  return [parseInt(parts[0], 10) || 0, parseInt(parts[1], 10) || 0];
};

const parseMockInt = (raw, fallback) => {
  const n = parseInt(String(raw || '').trim(), 10);
  return Number.isFinite(n) ? n : fallback;
};

const parseMockFloat = (raw, fallback) => {
  const n = parseFloat(String(raw || '').trim());
  return Number.isFinite(n) ? n : fallback;
};

const truncateRunes = (s, n) => {
  if (n <= 0) return '';
  const chars = Array.from(s);
  if (chars.length <= n) return s;
  return chars.slice(0, n).join('');
};

const buildTextOfLength = (g, n) => {
  let out = '';
  while (out.length < n) {
    if (out) out += ' ';
    out += g.word();
  }
  return out;
};

const evalMockCall = (call, seedParts) => {
  const seeds = ['mock', call.name];
  if (call.args.seed !== undefined && String(call.args.seed).trim() !== '') {
    seeds.push(call.args.seed);
  }
  seeds.push(...seedParts);
  const g = catalogIpsum(...seeds);
  switch (call.name) {
    case 'image':
    case 'img':
    case 'photo':
    case 'media': {
      let [w, h] = parseMockSize(call.args.size);
      if (w <= 0) w = parseMockInt(call.args.width, 400);
      if (h <= 0) h = parseMockInt(call.args.height, 500);
      return g.imageUrl(w, h);
    }
    case 'text':
    case 'string': {
      const n = parseMockInt(call.args.size, parseMockInt(call.args.length, 0));
      if (n <= 0) return g.text();
      return truncateRunes(buildTextOfLength(g, n), n);
    }
    case 'text_long':
    case 'textlong':
    case 'long_text':
    case 'paragraph':
      return g.textLong();
    case 'title':
    case 'heading':
      return g.title();
    case 'url':
    case 'link':
    case 'href':
      return `https://${g.word()}.example/${g.word()}`;
    case 'email':
    case 'mail':
      return `${g.word()}.${g.word()}@example.com`;
    case 'color':
    case 'colour':
      return g.hexColor();
    case 'number':
    case 'float':
    case 'price':
    case 'int':
    case 'integer': {
      const min = parseMockFloat(call.args.min, 1);
      const max = parseMockFloat(call.args.max, 999);
      return Math.round(g.number(min, max >= min ? max : min) * 100) / 100;
    }
    case 'boolean':
    case 'bool':
      return g.boolean();
    case 'icon':
    case 'emoji':
      return g.icon();
    case 'phone':
    case 'tel':
      return `(${10 + g.int(0, 89)}) ${g.int(1000, 9999)}-${g.int(1000, 9999)}`;
    case 'name':
    case 'person':
      return g.title();
    case 'word':
      return g.word();
    default:
      return g.text();
  }
};

export const isMockExpression = (raw) => String(raw || '').trim().toLowerCase().startsWith('mock.');

export const evaluateMockExpression = (expr, ...seedParts) => {
  const call = parseMockCall(expr);
  return evalMockCall(call, seedParts);
};

export const resolveMockDefault = (val, blockId, fieldKey, fieldType) => {
  if (typeof val === 'string' && isMockExpression(val)) {
    return evaluateMockExpression(val, 'field-default', blockId, fieldKey);
  }
  if (val !== undefined && val !== null && val !== '') return val;
  if (fieldType === 'boolean') return false;
  if (fieldType === 'number') return 0;
  if (fieldType === 'product_list' || fieldType === 'item_list') return [];
  return '';
};
